import { normalizeLedgerFields } from "./ledger-field-compat";
/**
 * Every call the app makes to its own API. Lives on its own rather than inside a feature, because
 * most of the screens need it and none of them need receipts.
 *
 * `path` is relative to the API root — `api("data")`, not `api("data")` — so a call site
 * cannot reach anywhere else by accident.
 *
 * A failure carries both things callers act on: `status`, to tell a signed-out 401 from a real
 * error, and `uncertain`. `uncertain: false` means the server is sure nothing was written, so a
 * retry is safe; anything else — a timeout, a dropped connection, a verify that could not finish —
 * leaves the write in doubt and has to be checked rather than repeated.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public uncertain: boolean,
    public status = 0,
  ) {
    super(message);
  }
}

export async function api<T = unknown>(path: string, body?: unknown) {
  let response: Response;
  try {
    response = await fetch("/api/" + path.replace(/^\/?(api\/)?/, ""), {
      method: body === undefined ? "GET" : "POST",
      headers: { "X-Barnyard-Fields": "title", ...(body === undefined ? {} : { "Content-Type": "application/json" }) },
      credentials: "same-origin",
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: AbortSignal.timeout(65000),
    });
  } catch (error) {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      throw new ApiError("You are offline. Connect to the internet to retry.", true, 0);
    }
    const isTimeout = error instanceof DOMException && error.name === "TimeoutError";
    throw new ApiError(
      isTimeout ? "Request timed out. Please try again." : "Could not reach the server. Please try again.",
      true,
      0,
    );
  }

  let value: (T & { error?: string; uncertain?: boolean }) | null = null;
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    try {
      value = (await response.json()) as T & { error?: string; uncertain?: boolean };
    } catch {
      value = null;
    }
  }

  if (!response.ok) {
    const errorText =
      value?.error ||
      (response.status === 503
        ? "Server is temporarily unavailable. Please try again shortly."
        : response.status === 502 || response.status === 504
        ? "Server gateway error. Please try again."
        : `Could not finish (HTTP ${response.status}). Please try again.`);
    throw new ApiError(
      errorText,
      value?.uncertain !== false,
      response.status,
    );
  }

  if (!value) {
    throw new ApiError("Invalid response from server.", true, response.status);
  }

  return normalizeLedgerFields(value);
}
